/**
 * Admin Tickets API Service
 * Handles ticket management CRUD operations
 */

import { get, post, patch, del } from '../client';
import { TicketOption } from '../../../types';

// === Request Types ===

export interface CreateTicketRequest {
  name: string;
  price: number;
  type: 'regular' | 'fast-track';
  description?: string;
  ticketFeatures?: string[];
  isActive?: boolean;
}

export interface UpdateTicketRequest extends CreateTicketRequest {
  isRecommended?: boolean;
  originalPrice?: number;
}

// === Response Types ===

interface ApiTicket {
  id: string;
  name: string;
  slug: string;
  price: number;
  originalPrice: number | null;
  type: 'regular' | 'fast-track';
  description: string | null;
  ticketFeatures: string | string[] | null;
  isActive: boolean;
  isRecommended: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TicketsResponse {
  message: string;
  data: ApiTicket[];
}

interface TicketResponse {
  message: string;
  data: ApiTicket;
}

// === Transform Functions ===

function transformTicket(apiTicket: ApiTicket): TicketOption {
  let features: string[] = [];
  
  if (apiTicket.ticketFeatures) {
    if (typeof apiTicket.ticketFeatures === 'string') {
      try {
        features = JSON.parse(apiTicket.ticketFeatures);
      } catch {
        features = [apiTicket.ticketFeatures];
      }
    } else if (Array.isArray(apiTicket.ticketFeatures)) {
      features = apiTicket.ticketFeatures;
    }
  }

  return {
    id: apiTicket.id,
    title: apiTicket.name,
    description: apiTicket.description || '',
    price: apiTicket.price,
    originalPrice: apiTicket.originalPrice ?? undefined,
    features,
    recommended: apiTicket.isRecommended,
    type: apiTicket.type,
  };
}

// === API Functions ===

/**
 * Get all tickets (admin view - includes inactive)
 */
export async function getAdminTickets(): Promise<TicketOption[]> {
  const response = await get<TicketsResponse>('/admin/tickets');
  return response.data.map(transformTicket);
}

/**
 * Get single ticket by ID
 */
export async function getAdminTicket(id: string): Promise<TicketOption> {
  const response = await get<TicketResponse>(`/admin/tickets/${id}`);
  return transformTicket(response.data);
}

/**
 * Create new ticket
 */
export async function createTicket(data: CreateTicketRequest): Promise<TicketOption> {
  const response = await post<TicketResponse>('/admin/tickets', data);
  return transformTicket(response.data);
}

/**
 * Update existing ticket
 */
export async function updateTicket(id: string, data: UpdateTicketRequest): Promise<TicketOption> {
  const response = await patch<TicketResponse>(`/admin/tickets/${id}`, data);
  return transformTicket(response.data);
}

/**
 * Delete ticket
 */
export async function deleteAdminTicket(id: string): Promise<void> {
  await del(`/admin/tickets/${id}`);
}
